import {
  useState,
  useEffect,
  useMemo,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import PipelineHeader from "./PipelineHeader";
import PipelineColumn from "./PipelineColumn";
import PipelineCard from "./PipelineCard";
import { colors } from "./Pipeline.styles";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { useSettings } from "../../../context/SettingsContext";
import { AuthContext } from "../../../context/AuthContext";
import { getUsersByCapabilitiesApi } from "../../../api/users.api";
import {
  updateGradeApi,
  getPipelineColumnSettingsApi,
  savePipelineColumnSettingsApi,
} from "../../../api/leads.api";
import AddLeadModal from "../Renderer/AddLeadModal";
import FilterPopover from "../Renderer/FilterPopover";
import ArchiveLeadsModal from "./ArchiveLeadsModal";
import ColumnSettingsModal from "./ColumnSettingsModal";
import { normalizeLead } from "../Renderer/LeadUtils";
// Bulk assign modal removed - using inline selection mode instead

const ALL_PIPELINE_STAGES = [
  // Pre-Consultation Pipeline
  {
    id: "qualified",
    title: "Qualified Leads",
    statusKey: "qualified",
    stage: "pre-consult",
  },
  {
    id: "need_analysis",
    title: "Need Analysis",
    statusKey: "need_analysis",
    stage: "pre-consult",
  },
  { id: "booked", title: "Booked", statusKey: "booked", stage: "pre-consult" },
  {
    id: "dropped",
    title: "Dropped",
    statusKey: "dropped",
    stage: "pre-consult",
  },

  // Post-Consultation Pipeline
  {
    id: "post_consult",
    title: "Post-Consult",
    statusKey: "post_consult",
    stage: "post-consult",
  },
  {
    id: "proposal_shared",
    title: "Proposal-Shared",
    statusKey: "proposal_shared",
    stage: "post-consult",
  },
  {
    id: "negotiation",
    title: "Negotiation",
    statusKey: "negotiation",
    stage: "post-consult",
  },
  {
    id: "treatment_booked",
    title: "Treatment-Booked",
    statusKey: "treatment_booked",
    stage: "post-consult",
  },
  { id: "won", title: "Won", statusKey: "won", stage: "post-consult" },
  {
    id: "dropped_post",
    title: "Dropped",
    statusKey: "dropped",
    stage: "post-consult",
  },
];

const Pipeline = ({ pipelineType = null }) => {
  const navigate = useNavigate();
  const { createLead, leads = [] } = useOutletContext();
  const { user } = useContext(AuthContext);
  const myUserId = user?._id || "";
  const { services = [], lead_features } = useSettings();
  const [searchParams] = useSearchParams();

  // Read and parse view parameter from URL (format: view=system:pre-consultation)
  const viewParam = searchParams.get("view");
  const hasRedirected = useRef(false);

  // Redirect to default view if no view parameter (only once)
  useEffect(() => {
    if (!viewParam && !pipelineType && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate("/pipelines?view=system:pre-consultation", { replace: true });
    }
  }, [viewParam, pipelineType, navigate]);

  // Calculate effective pipeline type from view parameter or prop
  const effectivePipelineType = useMemo(() => {
    if (viewParam && viewParam.startsWith("system:")) {
      const viewType = viewParam.replace("system:", "");
      if (viewType === "pre-consultation") {
        return "pre-consult";
      } else if (viewType === "post-consultation") {
        return "post-consult";
      }
    }
    return pipelineType;
  }, [viewParam, pipelineType]);

  // Filter states
  const [q, setQ] = useState("");
  const [dateRange, setDateRange] = useState("30");
  const [selectedSource, setSelectedSource] = useState("all");
  const [selectedTreatment, setSelectedTreatment] = useState("all");
  const [selectedTelecaller, setSelectedTelecaller] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [selectedResponse, setSelectedResponse] = useState("all");
  const [myLeadsOnly, setMyLeadsOnly] = useState(false);
  const [filtersOpenEl, setFiltersOpenEl] = useState(null);

  const [columns, setColumns] = useState({});
  const [columnSorts, setColumnSorts] = useState({}); // Track sort option for each column
  const [columnSettings, setColumnSettings] = useState({}); // Track settings for each column
  const [, setActiveId] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [leadManagers, setLeadManagers] = useState([]);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveColumnData, setArchiveColumnData] = useState(null);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [settingsColumnData, setSettingsColumnData] = useState(null);

  // Bulk Select Mode state - tracks which column is in selection mode
  const [bulkSelectColumnId, setBulkSelectColumnId] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);

  // Track insertion position during drag
  const [insertPosition, setInsertPosition] = useState(null);

  // Track which column is being hovered during bulk drag
  const [dragOverColumnId, setDragOverColumnId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  useEffect(() => {
    const fetchLeadManagers = async () => {
      try {
        const res = await getUsersByCapabilitiesApi(["lead_management"]);
        setLeadManagers(res?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch lead managers:", error);
      }
    };
    fetchLeadManagers();
  }, []);

  // Fetch user's column settings from backend
  useEffect(() => {
    const fetchColumnSettings = async () => {
      try {
        const response = await getPipelineColumnSettingsApi();

        if (response?.data?.data?.columnSettings) {
          setColumnSettings(response.data.data.columnSettings);
        }
      } catch (error) {
        console.error("Failed to fetch column settings:", error);
        // Continue with empty settings (defaults apply)
      }
    };

    fetchColumnSettings();
  }, []);

  // Reset filters when pipeline type changes
  useEffect(() => {
    setDateRange("30");
    setSelectedSource("all");
    setSelectedTreatment("all");
    setSelectedTelecaller("all");
    setSelectedPriority("all");
    setSelectedResponse("all");
    setMyLeadsOnly(false);
  }, [effectivePipelineType]);

  const telecallerOptions = useMemo(() => {
    const list = Array.isArray(leadManagers) ? leadManagers : [];
    return list
      .filter((u) => u && u._id && u.name)
      .map((u) => ({
        id: u._id,
        label: `${u.name}${u.team ? ` (${u.team})` : ""}`,
      }));
  }, [leadManagers]);

  const cap = useCallback(
    (s) =>
      String(s || "")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    [],
  );

  const treatments = useMemo(() => {
    const list = (services || []).filter((x) => x && x !== "—").map(cap);
    return ["all", ...new Set(list)];
  }, [services, cap]);

  const sourcesFromSettings = useMemo(() => {
    const list = lead_features?.sources || [];
    return [
      "all",
      ...new Set(
        list
          .map((s) => (typeof s === "string" ? s : s?.key || s?.name))
          .filter(Boolean),
      ),
    ];
  }, [lead_features]);



  // Default filter values
  const defaultFilters = {
    dateRange: "30",
    selectedSource: "all",
    selectedTreatment: "all",
    selectedTelecaller: "all",
    selectedPriority: "all",
    selectedResponse: "all",
    myLeadsOnly: false,
  };

  // Pending filters state for the modal
  const [pending, setPending] = useState(defaultFilters);

  // Filter stages based on effectivePipelineType
  const visibleStages = useMemo(() => {
    if (effectivePipelineType === "pre-consult") {
      return ALL_PIPELINE_STAGES.filter((s) => s.stage === "pre-consult");
    }
    if (effectivePipelineType === "post-consult") {
      return ALL_PIPELINE_STAGES.filter((s) => s.stage === "post-consult");
    }
    // Show all stages by default
    return ALL_PIPELINE_STAGES;
  }, [effectivePipelineType]);

  // Normalize leads to match Renderer's data structure
  const normalizedLeads = useMemo(
    () => (Array.isArray(leads) ? leads.map(normalizeLead) : []),
    [leads],
  );

  // Filter leads based on selected filters
  const filteredLeads = useMemo(() => {
    const rangeToDays = (key) => ({ 7: 7, 30: 30, 90: 90 })[key] || 30;
    const qq = q.trim().toLowerCase();

    return normalizedLeads.filter((l) => {
      // Search filter
      if (qq) {
        const searchTxt = `${l.name} ${l.phone} ${l.email} ${l.source}`.toLowerCase();
        if (!searchTxt.includes(qq)) return false;
      }

      // Date range filter
      const d = new Date(l.createdAt);
      const start = new Date();
      start.setDate(start.getDate() - rangeToDays(dateRange));
      if (l.createdAt && d < start) return false;

      // Source filter
      const source = l.source || "";
      if (
        selectedSource !== "all" &&
        String(source).toLowerCase() !== selectedSource.toLowerCase()
      )
        return false;

      // Treatment filter
      const leadServices = Array.isArray(l.services) ? l.services : [];
      if (selectedTreatment !== "all") {
        if (
          !leadServices.some(
            (s) => String(s).toLowerCase() === selectedTreatment.toLowerCase(),
          )
        )
          return false;
      }

      // Telecaller filter
      const leadTelecallerId = l.telecallerId || "";
      if (myLeadsOnly) {
        if (!myUserId || leadTelecallerId !== myUserId) return false;
      } else {
        if (
          selectedTelecaller !== "all" &&
          leadTelecallerId !== selectedTelecaller
        )
          return false;
      }

      // Priority filter
      const pr = String(l.priority || "").toUpperCase();
      if (selectedPriority !== "all" && pr !== selectedPriority) return false;

      // Pipeline type filter - only show leads whose status belongs to current pipeline view
      const currentStatusKey = l.current_status?.key || "";
      const currentStatusType = l.current_status?.type || "";

      // Only include leads with pipeline status type
      if (currentStatusType !== "pipeline") return false;

      // Check if the lead's status key exists in the current visible stages
      const statusInVisibleStages = visibleStages.some((s) => s.statusKey === currentStatusKey);
      if (!statusInVisibleStages) return false;

      return true;
    });
  }, [
    normalizedLeads,
    q,
    dateRange,
    selectedSource,
    selectedTreatment,
    selectedTelecaller,
    selectedPriority,
    myLeadsOnly,
    myUserId,
    visibleStages,
  ]);

  useEffect(() => {
    // Organize filtered leads by stage
    const organizedColumns = {};
    visibleStages.forEach((stage) => {
      organizedColumns[stage.id] = [];
    });

    filteredLeads.forEach((lead) => {
      const statusKey = lead.current_status?.key;
      const statusType = lead.current_status?.type;

      // Match lead to stage based on both statusKey and type
      const stage = visibleStages.find(
        (s) =>
          s.statusKey === statusKey &&
          (statusType === "pipeline" || s.id === "new"),
      );

      if (stage) {
        organizedColumns[stage.id].push(lead);
      } else if (pipelineType === "pre-consult") {
        // Only add to pre-consult stages
        const preStage = visibleStages.find((s) => s.id === "qualified");
        if (preStage) organizedColumns.qualified.push(lead);
      } else if (pipelineType === "post-consult") {
        // Only add to post-consult stages
        const postStage = visibleStages.find((s) => s.id === "post_consult");
        if (postStage) organizedColumns.post_consult.push(lead);
      } else {
        // Add to qualified (first stage) by default
        if (organizedColumns.qualified) {
          organizedColumns.qualified.push(lead);
        }
      }
    });

    setColumns(organizedColumns);
  }, [effectivePipelineType, visibleStages, filteredLeads, pipelineType]);

  const handleDragStart = useCallback(
    (event) => {
      const activeId = event.active.id;
      setActiveId(activeId);
      setInsertPosition(null);
      // Capture the lead at drag start
      for (const columnLeads of Object.values(columns)) {
        const lead = columnLeads.find((l) => l._id === activeId);
        if (lead) {
          setActiveLead(lead);
          break;
        }
      }
    },
    [columns],
  );

  const findColumnByLeadId = useCallback(
    (leadId) => {
      for (const [columnId, columnLeads] of Object.entries(columns)) {
        if (columnLeads.some((lead) => lead._id === leadId)) {
          return columnId;
        }
      }
      return null;
    },
    [columns],
  );

  const handleDragMove = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over) {
        setInsertPosition(null);
        setDragOverColumnId(null);
        return;
      }

      const activeId = active.id;
      const overId = over.id;

      const activeColumn = findColumnByLeadId(activeId);
      let overColumn = findColumnByLeadId(overId);

      // If overId is a column itself
      if (!overColumn && visibleStages.find((s) => s.id === overId)) {
        overColumn = overId;
      }

      if (!activeColumn || !overColumn) {
        setInsertPosition(null);
        setDragOverColumnId(null);
        return;
      }

      // Track which column we're over for bulk drag drop zone
      const isBulkDrag = selectedLeadIds.length > 1 && selectedLeadIds.includes(activeId);
      if (isBulkDrag && activeColumn !== overColumn) {
        setDragOverColumnId(overColumn);
      } else {
        setDragOverColumnId(null);
      }

      // Calculate insert index
      const overItems = columns[overColumn] || [];
      const overIndex = overItems.findIndex((item) => item._id === overId);

      // Determine if inserting before or after
      const overRect = over.rect;
      const activeRect = active.rect;

      let insertIndex;
      if (overIndex === -1) {
        // Dragging over empty column - insert at end
        insertIndex = overItems.length;
      } else {
        const overMiddleY = overRect.top + overRect.height / 2;
        const draggedCenterY = activeRect.top + activeRect.height / 2;
        const insertAfter = draggedCenterY > overMiddleY;

        insertIndex = insertAfter ? overIndex + 1 : overIndex;
      }

      setInsertPosition({
        columnId: overColumn,
        index: insertIndex,
      });
    },
    [columns, visibleStages, findColumnByLeadId, selectedLeadIds],
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);
      setActiveLead(null);
      setInsertPosition(null);
      setDragOverColumnId(null);

      if (!over) return;

      const activeId = active.id;
      const overId = over.id;

      const activeColumn = findColumnByLeadId(activeId);
      let overColumn = findColumnByLeadId(overId);

      // If overId is a column itself, use it
      if (!overColumn && visibleStages.find((s) => s.id === overId)) {
        overColumn = overId;
      }

      if (!activeColumn || !overColumn) return;

      const newColumns = { ...columns };

      // Check if we're moving selected leads (bulk move)
      const isMultiSelect = selectedLeadIds.length > 1 && selectedLeadIds.includes(activeId);

      if (isMultiSelect && activeColumn !== overColumn) {
        // Multi-card move to different column
        const leadsToMove = selectedLeadIds;
        const activeItems = [...newColumns[activeColumn]];
        const overItems = [...newColumns[overColumn]];

        // Get all leads being moved (maintain their original order)
        const movedLeads = [];
        leadsToMove.forEach((leadId) => {
          const leadIndex = activeItems.findIndex((item) => item._id === leadId);
          if (leadIndex !== -1) {
            const [lead] = activeItems.splice(leadIndex, 1);
            movedLeads.push(lead);
          }
        });

        // Add to destination column at the FIRST position (top)
        overItems.unshift(...movedLeads);

        newColumns[activeColumn] = activeItems;
        newColumns[overColumn] = overItems;

        // Update status for all moved leads
        const newStage = visibleStages.find((s) => s.id === overColumn);
        if (newStage && newStage.statusKey) {
          movedLeads.forEach((lead) => {
            lead.current_status = {
              key: newStage.statusKey,
              type: "pipeline",
            };

            updateGradeApi(lead._id, {
              key: newStage.statusKey,
              type: "pipeline",
            }).catch((error) => {
              console.error("Failed to update lead status:", error);
            });
          });
        }

        // Clear selection after bulk move
        setBulkSelectColumnId(null);
        setSelectedLeadIds([]);
      } else if (activeColumn === overColumn) {
        // Handle within-column reordering (single card)
        const items = [...newColumns[activeColumn]];
        const activeIndex = items.findIndex((item) => item._id === activeId);
        const overIndex = items.findIndex((item) => item._id === overId);

        if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex)
          return;

        const [movedItem] = items.splice(activeIndex, 1);
        items.splice(overIndex, 0, movedItem);
        newColumns[activeColumn] = items;
      } else {
        // Handle single-card cross-column move
        const activeItems = [...newColumns[activeColumn]];
        const overItems = [...newColumns[overColumn]];

        const activeIndex = activeItems.findIndex(
          (item) => item._id === activeId,
        );
        if (activeIndex === -1) return;

        const [movedItem] = activeItems.splice(activeIndex, 1);

        const overIndex = overItems.findIndex((item) => item._id === overId);

        if (overIndex === -1) {
          overItems.push(movedItem);
        } else {
          const overRect = over.rect;
          const activeRect = active.rect;

          const overMiddleY = overRect.top + overRect.height / 2;
          const draggedCenterY = activeRect.top + activeRect.height / 2;
          const insertAfter = draggedCenterY > overMiddleY;

          if (insertAfter) {
            overItems.splice(overIndex + 1, 0, movedItem);
          } else {
            overItems.splice(overIndex, 0, movedItem);
          }
        }

        newColumns[activeColumn] = activeItems;
        newColumns[overColumn] = overItems;

        // Update lead status in backend when moving to a different column
        const newStage = visibleStages.find((s) => s.id === overColumn);
        if (newStage && newStage.statusKey) {
          // Update the moved item's current_status locally to prevent stale data on re-render
          movedItem.current_status = {
            key: newStage.statusKey,
            type: "pipeline",
          };

          updateGradeApi(activeId, {
            key: newStage.statusKey,
            type: "pipeline",
          }).catch((error) => {
            console.error("Failed to update lead status:", error);
          });
        }
      }

      setColumns(newColumns);
    },
    [columns, visibleStages, findColumnByLeadId, selectedLeadIds],
  );

  const handleViewDetails = useCallback(
    (lead) => {
      navigate(`/pipelines/${lead._id}`);
    },
    [navigate],
  );

  const handleContactClick = useCallback((contactInfo) => {
    console.log("Contact clicked:", contactInfo);
  }, []);

  const handleAddLead = useCallback(() => {
    setAddLeadOpen(true);
  }, []);

  // Apply pending filters
  const applyPending = useCallback(() => {
    setDateRange(pending.dateRange);
    setSelectedSource(pending.selectedSource);
    setSelectedTreatment(pending.selectedTreatment);
    setSelectedTelecaller(pending.selectedTelecaller);
    setSelectedPriority(pending.selectedPriority);
    setSelectedResponse(pending.selectedResponse);
    setMyLeadsOnly(!!pending.myLeadsOnly);
    setFiltersOpenEl(null);
  }, [pending]);

  const handleCardMenuAction = useCallback(
    (action, lead) => {
      console.log(`Card menu action: ${action} for lead:`, lead.name);

      // Handle stage movement actions
      if (action === "move-next-stage" || action === "move-prev-stage") {
        // Find current column of the lead
        const currentColumnId = findColumnByLeadId(lead._id);
        if (!currentColumnId) return;

        // Find current column index
        const currentIndex = visibleStages.findIndex((s) => s.id === currentColumnId);
        if (currentIndex === -1) return;

        // Calculate target column index
        let targetIndex;
        if (action === "move-next-stage") {
          targetIndex = currentIndex + 1;
          // Don't move if already at last column
          if (targetIndex >= visibleStages.length) return;
        } else {
          targetIndex = currentIndex - 1;
          // Don't move if already at first column
          if (targetIndex < 0) return;
        }

        const targetColumnId = visibleStages[targetIndex].id;
        const targetStage = visibleStages[targetIndex];

        // Update columns state
        setColumns((prevColumns) => {
          const newColumns = { ...prevColumns };
          const sourceItems = [...(newColumns[currentColumnId] || [])];
          const targetItems = [...(newColumns[targetColumnId] || [])];

          // Find and remove lead from source column
          const leadIndex = sourceItems.findIndex((item) => item._id === lead._id);
          if (leadIndex === -1) return prevColumns;

          const [movedLead] = sourceItems.splice(leadIndex, 1);

          // Update lead's current_status
          movedLead.current_status = {
            key: targetStage.statusKey,
            type: "pipeline",
          };

          // Add to target column at the first position
          targetItems.unshift(movedLead);

          newColumns[currentColumnId] = sourceItems;
          newColumns[targetColumnId] = targetItems;

          return newColumns;
        });

        // Update backend
        updateGradeApi(lead._id, {
          key: targetStage.statusKey,
          type: "pipeline",
        }).catch((error) => {
          console.error("Failed to update lead status:", error);
        });
      }

      // Handle view profile action
      if (action === "view-profile") {
        navigate(`/pipelines/${lead._id}`);
      }
    },
    [findColumnByLeadId, visibleStages, navigate],
  );

  const handleColumnMenuAction = useCallback((action, columnId, sortOption) => {
    console.log(`Column menu action: ${action} for column:`, columnId);

    if (action === "sort-by" && sortOption) {
      setColumnSorts((prev) => ({
        ...prev,
        [columnId]: sortOption,
      }));
    } else if (action === "archive-leads") {
      const stage = visibleStages.find((s) => s.id === columnId);
      const leadsInColumn = columns[columnId] || [];
      setArchiveColumnData({
        columnId,
        columnTitle: stage?.title || columnId,
        leadsCount: leadsInColumn.length,
      });
      setArchiveModalOpen(true);
    } else if (action === "column-settings") {
      const stage = visibleStages.find((s) => s.id === columnId);
      setSettingsColumnData({
        columnId,
        columnTitle: stage?.title || columnId,
      });
      setColumnSettingsOpen(true);
    } else if (action === "bulk-action") {
      // Toggle bulk select mode for this column
      if (bulkSelectColumnId === columnId) {
        // Already in bulk select mode for this column, turn it off
        setBulkSelectColumnId(null);
        setSelectedLeadIds([]);
      } else {
        // Enable bulk select mode for this column
        setBulkSelectColumnId(columnId);
        setSelectedLeadIds([]);
      }
    }
  }, [columns, visibleStages, bulkSelectColumnId]);

  const handleArchiveConfirm = useCallback(() => {
    if (archiveColumnData) {
      console.log(`Archiving ${archiveColumnData.leadsCount} leads from column:`, archiveColumnData.columnTitle);
      // TODO: Implement actual archive API call
    }
  }, [archiveColumnData]);

  const handleColumnSettingsSave = useCallback(
    async (settings) => {
      if (settingsColumnData) {
        // Optimistic update - update UI immediately
        const newColumnSettings = {
          ...columnSettings,
          [settingsColumnData.columnId]: settings,
        };
        setColumnSettings(newColumnSettings);

        // Save to backend asynchronously
        try {
          await savePipelineColumnSettingsApi({
            columnSettings: newColumnSettings,
          });
        } catch (error) {
          console.error("Failed to save column settings:", error);
          // Settings remain in local state for this session
        }
      }
    },
    [settingsColumnData, columnSettings]
  );

  // Handle lead selection toggle in bulk select mode
  const handleSelectToggle = useCallback((lead) => {
    setSelectedLeadIds((prev) => {
      if (prev.includes(lead._id)) {
        return prev.filter((id) => id !== lead._id);
      }
      return [...prev, lead._id];
    });
  }, []);


  return (
    <Box
      sx={{
        height: "100%",
        maxHeight: "calc(100vh - 16px)",
        bgcolor: colors.bgPrimary,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ px: 3, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <PipelineHeader
          searchValue={q}
          onSearchChange={setQ}
          onAddLead={handleAddLead}
          onFilter={(e) => {
            setPending({
              dateRange,
              selectedSource,
              selectedTreatment,
              selectedTelecaller,
              selectedPriority,
              selectedResponse,
              myLeadsOnly,
            });
            setFiltersOpenEl(e.currentTarget);
          }}
          pipelineType={effectivePipelineType}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,

          overflowX: "auto",
          overflowY: "hidden",
          minHeight: "80vh",
          "&::-webkit-scrollbar": {
            height: "8px",
            mt: 2,
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "#FFFFFF",
            mt: 2,
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: colors.primary,
            borderRadius: "4px",
            "&:hover": {
              bgcolor: colors.primaryDark,
            },
          },
        }}
      >
        {q.trim() && filteredLeads.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
              px: 3,
              width: "100%",
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#64748B", mb: 0.5 }}>
              No results found
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#94A3B8" }}>
              No leads match "{q.trim()}"
            </Typography>
          </Box>
        ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              height: "100%",
              width: "max-content",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{
                height: "100%",
                px: 3,
                pb: 2,
                alignItems: "stretch",
              }}
            >
              {visibleStages.map((stage, index) => (
                <PipelineColumn
                  key={stage.id}
                  title={stage.title}
                  leads={columns[stage.id] || []}
                  columnId={stage.id}
                  sortOption={columnSorts[stage.id] || "date-newest"}
                  columnSettings={columnSettings[stage.id]}
                  onViewDetails={handleViewDetails}
                  onContactClick={handleContactClick}
                  onAddLead={handleAddLead}
                  onMenuAction={handleCardMenuAction}
                  onColumnMenuAction={handleColumnMenuAction}
                  insertPosition={
                    insertPosition?.columnId === stage.id
                      ? insertPosition
                      : null
                  }
                  bulkSelectMode={bulkSelectColumnId === stage.id}
                  selectedLeadIds={selectedLeadIds}
                  onSelectToggle={handleSelectToggle}
                  showDropZone={dragOverColumnId === stage.id}
                  dropZoneCount={selectedLeadIds.length}
                  isFirstColumn={index === 0}
                  isLastColumn={index === visibleStages.length - 1}
                />
              ))}
            </Stack>
          </Box>

          <DragOverlay>
            {activeLead ? (
              <Box
                sx={{
                  opacity: 0.9,
                  transform: "rotate(3deg)",
                }}
              >
                <PipelineCard
                  lead={activeLead}
                  onViewDetails={handleViewDetails}
                  onContactClick={handleContactClick}
                  isDragging
                />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
        )}
      </Box>

      <AddLeadModal
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        telecallers={telecallerOptions}
        treatments={treatments}
        sources={sourcesFromSettings}
        onSave={async (data) => {
          const payload = {
            client_name: data.fullName?.trim(),
            contact: data.phone?.trim(),
            email: data.email?.trim(),
            assign_employee: data.telecaller || undefined,
            services: Array.isArray(data.treatmentTypes)
              ? data.treatmentTypes
              : [],
            custom_fields: {
              lead_source: data.source || undefined,
              city: data.location || undefined,
              message: data.notes || undefined,
            },
          };

          await createLead(payload);
        }}
      />

      <FilterPopover
        open={Boolean(filtersOpenEl)}
        anchorEl={filtersOpenEl}
        onClose={() => setFiltersOpenEl(null)}
        pending={pending}
        setPending={setPending}
        applyPending={applyPending}
        clearPending={() => setPending(defaultFilters)}
        treatments={treatments}
        telecallers={telecallerOptions}
        sources={sourcesFromSettings}
        hideResponseLabel={true}
      />

      <ArchiveLeadsModal
        open={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        columnTitle={archiveColumnData?.columnTitle || ""}
        leadsCount={archiveColumnData?.leadsCount || 0}
        onConfirm={handleArchiveConfirm}
      />

      <ColumnSettingsModal
        open={columnSettingsOpen}
        onClose={() => setColumnSettingsOpen(false)}
        columnTitle={settingsColumnData?.columnTitle || ""}
        currentSettings={settingsColumnData?.columnId ? columnSettings[settingsColumnData.columnId] : null}
        onSave={handleColumnSettingsSave}
      />
    </Box>
  );
};

export default Pipeline;
